import React, { useState, useCallback, useEffect } from "react";
import { useAsync } from "./hooks";
import { api } from "./api";
import { NAV, I } from "./utils";
import { ToastProvider, ConfirmProvider, Icon } from "./ui";
import { AuthProvider, RequireAuth, useAuth } from "./auth";
import { BRAND, FONT_CSS } from "./brand";
import { BRAND_HEX, BRAND_LIGHT, BRAND_GRADIENT, THEME_CSS_VARS, brandAlpha } from "./theme"; // colors live in theme.js
import { DashboardTab } from "./DashboardTab";
import { ClientsTab }   from "./clients/ClientsTab";
import { TripsTab }     from "./trips/TripsTab";
import { TasksTab }     from "./tasks/TasksTab";
import { LeadsTab }     from "./leads/LeadsTab";
import { FinancesTab }  from "./finances/FinancesTab";
import { TeamsTab }     from "./teams/TeamsTab";
import { InvoicesTab }  from "./invoices/InvoicesTab";
import { ReceiptsTab }  from "./receipts/ReceiptsTab";
import {RefundsTab} from "./refund/RefundsTab";
import { ReviewsTab } from "./reviews/ReviewsTab";
import PublicReviewPage, { getReviewToken } from "./reviews/PublicReviewPage";

// ─── APP SHELL ────────────────────────────────────────────────────────────────
function AppShell() {
  const [tab, setTab]   = useState("dashboard");
  const [open, setOpen] = useState(false);
  const { data: stats } = useAsync(() => api.get("/crm/dashboard/stats"));
  const { user, logout } = useAuth();

  const nav = useCallback(id => { setTab(id); setOpen(false); }, []);

  // Browser tab title follows the brand config.
  useEffect(() => { document.title = BRAND.appName; }, []);

  const renderTab = () => {
    switch (tab) {
      case "dashboard": return <DashboardTab onNavigate={nav} />;
      case "clients":   return <ClientsTab />;
      case "trips":     return <TripsTab />;
      case "tasks":     return <TasksTab />;
      case "leads":     return <LeadsTab />;
      case "teams":     return <TeamsTab />;
      case "invoices":  return <InvoicesTab />;
      case "receipts":  return <ReceiptsTab />;
      case "refunds":  return <RefundsTab />;
      case "reviews":  return <ReviewsTab />;
      case "finances":  return <FinancesTab />;
      default:          return <DashboardTab onNavigate={nav} />;
    }
  };

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black/30 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* ── Sidebar ── */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-60 bg-white border-r border-slate-100 flex flex-col transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 flex-shrink-0`}>

        {/* Logo */}
        <div className="px-6 py-5 border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: BRAND_GRADIENT }}
            >
              <Icon d={I.plane} size={16} className="text-white" />
            </div>
            <div>
              <p className="font-bold text-slate-800 text-sm leading-tight" style={{ fontFamily: "var(--font-heading)" }}>
                {BRAND.appName}
              </p>
              <p className="text-xs text-slate-400">{BRAND.tagline}</p>
            </div>
          </div>
        </div>

        {/* Nav links */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {NAV.map(n => (
            <button
              key={n.id}
              onClick={() => nav(n.id)}
              className={`sidebar-link w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-500 ${tab === n.id ? "active" : ""}`}
            >
              <Icon d={I[n.icon]} size={18} />
              {n.label}

              {/* Overdue tasks badge */}
              {n.id === "tasks" && stats?.overdueTasks > 0 && tab !== "tasks" && (
                <span className="ml-auto w-5 h-5 rounded-full bg-rose-500 text-white text-xs flex items-center justify-center font-bold">
                  {stats.overdueTasks}
                </span>
              )}

              {/* New leads badge — brand color from token */}
              {n.id === "leads" && stats?.newLeads > 0 && tab !== "leads" && (
                <span
                  className="ml-auto w-5 h-5 rounded-full text-white text-xs flex items-center justify-center font-bold"
                  style={{ background: BRAND_HEX }}
                >
                  {stats.newLeads}
                </span>
              )}
            </button>
          ))}
        </nav>

        {/* User footer */}
        <div className="px-4 py-4 border-t border-slate-100">
          <div className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
              style={{ background: BRAND_GRADIENT }}
            >
              {(user?.[0] || "?").toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-slate-700 truncate capitalize">
                {user ? user.split("@")[0] : "User"}
              </p>
              <p className="text-xs text-slate-400 truncate">{user || ""}</p>
            </div>
            <button
              onClick={logout}
              title="Sign out"
              className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-rose-500 transition-colors flex-shrink-0"
            >
              <Icon d={I.logout} size={14} />
            </button>
          </div>
        </div>
      </aside>

      {/* ── Main content ── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

        {/* Header */}
        <header className="bg-white border-b border-slate-100 px-5 py-3.5 flex items-center gap-3 flex-shrink-0">
          <button
            onClick={() => setOpen(true)}
            className="lg:hidden w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500"
          >
            <Icon d={I.menu} size={18} />
          </button>

          <div className="flex-1 relative hidden sm:block max-w-sm">
            <Icon d={I.search} size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" />
            {/* focus ring uses BRAND_HEX inline so it tracks the token */}
            <input
              placeholder="Quick search…"
              className="w-full pl-8 pr-4 py-2 rounded-xl border border-slate-200 text-sm outline-none bg-slate-50 transition-colors"
              style={{ "--tw-ring-color": `${BRAND_HEX}4d` }}   // 4d = 30% opacity hex
              onFocus={e  => { e.target.style.borderColor = BRAND_HEX; e.target.style.boxShadow = `0 0 0 3px ${BRAND_HEX}30`; }}
              onBlur={e   => { e.target.style.borderColor = ""; e.target.style.boxShadow = ""; }}
            />
          </div>

          <div className="ml-auto">
            <button className="relative w-9 h-9 rounded-xl border border-slate-200 flex items-center justify-center text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-colors">
              <Icon d={I.bell} size={18} />
              {stats?.overdueTasks > 0 && (
                <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-rose-500" />
              )}
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-5 py-6">{renderTab()}</div>
        </main>
      </div>
    </div>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────
export default function CrmDashboard() {
  // A client opening their review link (…/?review=<token>) gets the review form only —
  // no login, no CRM.
  const reviewToken = getReviewToken();
  return (
    <>
      <style>{`
        ${FONT_CSS}
        ${THEME_CSS_VARS}
        * { font-family: var(--font-body); box-sizing: border-box; }
        h1, h2, h3 { font-family: var(--font-heading); }
        @keyframes modalIn { from { opacity: 0; transform: translateY(16px) scale(.97); } to { opacity: 1; transform: none; } }
        @keyframes toastIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }

        /* ── Sidebar active / hover states — sourced from theme token ── */
        .sidebar-link { transition: all .15s ease; }
        .sidebar-link:hover  { background: ${BRAND_LIGHT}; }
        .sidebar-link.active { background: ${brandAlpha(0.15)}; color: ${BRAND_HEX}; }
      `}</style>
      {reviewToken ? (
        <PublicReviewPage token={reviewToken} />
      ) : (
        <ToastProvider>
          <ConfirmProvider>
            <AuthProvider>
              <RequireAuth>
                <AppShell />
              </RequireAuth>
            </AuthProvider>
          </ConfirmProvider>
        </ToastProvider>
      )}
    </>
  );
}